import React from 'react';
import { MultiSelect } from 'primereact/multiselect';
import { ProgressSpinner } from 'primereact/progressspinner';
import { handleApiResponse } from 'utilities/apiUtils';

type RawOption = { label: string; value: string };

export type Step2Props = {
  selectedVehicles: string[];
  setSelectedVehicles: (v: string[]) => void;
  selectedDrivers: string[];
  setSelectedDrivers: (v: string[]) => void;
  selectedEquipment: string[];
  setSelectedEquipment: (v: string[]) => void;
  selectedProperties: string[];
  setSelectedProperties: (v: string[]) => void;
  insuredDatabaseId?: string;
  masterCertPolicyIds?: string[];
};

export interface Step2Ref { validate: () => boolean; }

const Step2 = React.forwardRef<Step2Ref, Step2Props>(function Step2Inner({
  selectedVehicles,
  setSelectedVehicles,
  selectedDrivers,
  setSelectedDrivers,
  selectedEquipment,
  setSelectedEquipment,
  selectedProperties,
  setSelectedProperties,
  insuredDatabaseId,
  masterCertPolicyIds = [],
}: Step2Props, ref: React.Ref<Step2Ref>) {
  const [vehicleOptions, setVehicleOptions] = React.useState<RawOption[]>([]);
  const [loadingVehicles, setLoadingVehicles] = React.useState(false);
  const [driverOptions, setDriverOptions] = React.useState<RawOption[]>([]);
  const [loadingDrivers, setLoadingDrivers] = React.useState(false);
  const [equipmentOptions, setEquipmentOptions] = React.useState<{ label: string; value: string }[]>([]);
  const [loadingEquipment, setLoadingEquipment] = React.useState(true);
  const [propertyOptions, setPropertyOptions] = React.useState<RawOption[]>([]);
  const [loadingProperties, setLoadingProperties] = React.useState(false);

  React.useImperativeHandle(ref, () => ({
    validate: () => true,
  }));

  React.useEffect(() => {
    if (!insuredDatabaseId || !masterCertPolicyIds.length) {
      setVehicleOptions([]);
      return;
    }
    setLoadingVehicles(true);
    const ncToken = localStorage.getItem('NCToken') || '';
    const authToken = localStorage.getItem('igtoken') || '';
    fetch(`${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/insured-vehicles?insuredDatabaseId=${insuredDatabaseId}&policyIds=${encodeURIComponent(masterCertPolicyIds.join(','))}`, {
      headers: {
        'NCToken': ncToken,
        'Accept': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      }
    })
      .then(res => handleApiResponse(res))
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data.value)) {
          setVehicleOptions(data.value.map((vehicle: any) => {
            const parts = [vehicle.make, vehicle.model].filter(Boolean).join(' ');
            const label = parts && vehicle.vin ? `${parts} (${vehicle.vin})` : parts || vehicle.vin || vehicle.typeDescription;
            return { label, value: vehicle.databaseId };
          }));
        } else {
          setVehicleOptions([]);
        }
      })
      .catch(() => setVehicleOptions([]))
      .finally(() => setLoadingVehicles(false));
  }, [insuredDatabaseId, masterCertPolicyIds]);

  React.useEffect(() => {
    if (!insuredDatabaseId || !masterCertPolicyIds.length) {
      setDriverOptions([]);
      return;
    }
    setLoadingDrivers(true);
    const ncToken = localStorage.getItem('NCToken') || '';
    const authToken = localStorage.getItem('igtoken') || '';
    fetch(`${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/insured-drivers?insuredDatabaseId=${insuredDatabaseId}&policyIds=${encodeURIComponent(masterCertPolicyIds.join(','))}`, {
      headers: {
        'NCToken': ncToken,
        'Accept': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      }
    })
      .then(res => handleApiResponse(res))
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data.value)) {
          setDriverOptions(data.value.map((driver: any) => {
            const name = [driver.firstName, driver.lastName].filter(Boolean).join(' ');
            const label = name && driver.licenseNumber ? `${name} (${driver.licenseNumber})` : name || driver.licenseNumber || 'Driver';
            return { label, value: driver.databaseId };
          }));
        } else {
          setDriverOptions([]);
        }
      })
      .catch(() => setDriverOptions([]))
      .finally(() => setLoadingDrivers(false));
  }, [insuredDatabaseId, masterCertPolicyIds]);

  React.useEffect(() => {
    setLoadingEquipment(true);
    const ncToken = localStorage.getItem('NCToken') || '';
    const authToken = localStorage.getItem('igtoken') || '';
    fetch(`${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/insured-equipment?insuredDatabaseId=${encodeURIComponent(insuredDatabaseId || '')}&top=10`, {
      headers: {
        'NCToken': ncToken,
        'Accept': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      }
    })
      .then(res => handleApiResponse(res))
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data.value)) {
          setEquipmentOptions(data.value.map((equipment: any) => {
            const parts = [equipment.type, equipment.modelYear].filter(Boolean).join(' ');
            const label = parts && equipment.idSerialNumber ? `${parts} (${equipment.idSerialNumber})` : parts || equipment.idSerialNumber || equipment.description || equipment.unitNumber || 'Equipment';
            return { label, value: equipment.databaseId };
          }));
        } else {
          setEquipmentOptions([]);
        }
      })
      .catch(() => {
        setEquipmentOptions([]);
      })
      .finally(() => setLoadingEquipment(false));
  }, [insuredDatabaseId]);

  React.useEffect(() => {
    if (!insuredDatabaseId || !masterCertPolicyIds.length) {
      setPropertyOptions([]);
      return;
    }
    setLoadingProperties(true);
    const ncToken = localStorage.getItem('NCToken') || '';
    const authToken = localStorage.getItem('igtoken') || '';
    const url = `${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/insured-properties?top=100&insuredDatabaseId=${encodeURIComponent(insuredDatabaseId)}&policyIds=${encodeURIComponent(masterCertPolicyIds.join(','))}`;
    fetch(url, {
      headers: {
        'NCToken': ncToken,
        'Accept': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      }
    })
      .then(res => handleApiResponse(res))
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data.value)) {
          setPropertyOptions(data.value.map((property: any) => {
            const locBldg = [property.locationNumber, property.buildingNumber].filter(Boolean).join(',');
            const addr = [property.addressLine1, property.city, property.zip].filter(Boolean).join(', ');
            const base = [locBldg, addr].filter(Boolean).join(', ');
            const dateStr = property.changeDate ? new Date(property.changeDate).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) : '';
            const status = property.active ? 'Active' : 'Inactive';
            const suffix = dateStr ? `(${dateStr},${status})` : `(${status})`;
            const label = base ? `${base} ${suffix}` : (property.description || property.propertyUse || 'Property');
            return { label, value: property.databaseId };
          }));
        } else {
          setPropertyOptions([]);
        }
      })
      .catch(() => setPropertyOptions([]))
      .finally(() => setLoadingProperties(false));
  }, [insuredDatabaseId, masterCertPolicyIds]);

  return (
    <div className="page">
      <div className="page__sectionHeader">
        <div>
          <h2 className="page__title">Insured items</h2>
          <p className="page__muted">Select the insured items needed for the form</p>
        </div>
      </div>
      <div className="page__twoCol" style={{ gridTemplateColumns: '1fr' }}>
        <div className="pageCard" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div>
            <div className="page__muted" style={{ marginBottom: 4 }}>Vehicles</div>
            <div style={{ position: 'relative' }}>
              <MultiSelect value={selectedVehicles} options={vehicleOptions} onChange={e => setSelectedVehicles(e.value)} placeholder={loadingVehicles ? "Loading..." : "Select vehicles"} display="chip" maxSelectedLabels={2} selectedItemsLabel="{0} selected" style={{ width: '100%' }} disabled={loadingVehicles} />
              {loadingVehicles && (
                <ProgressSpinner style={{ width: '20px', height: '20px', position: 'absolute', right: '40px', top: '50%', transform: 'translateY(-50%)' }} />
              )}
            </div>
          </div>
          <div>
            <div className="page__muted" style={{ marginBottom: 4 }}>Drivers</div>
            <div style={{ position: 'relative' }}>
              <MultiSelect value={selectedDrivers} options={driverOptions} onChange={e => setSelectedDrivers(e.value)} placeholder={loadingDrivers ? "Loading..." : "Select drivers"} display="chip" maxSelectedLabels={2} selectedItemsLabel="{0} selected" style={{ width: '100%' }} disabled={loadingDrivers} />
              {loadingDrivers && (
                <ProgressSpinner style={{ width: '20px', height: '20px', position: 'absolute', right: '40px', top: '50%', transform: 'translateY(-50%)' }} />
              )}
            </div>
          </div>
          {/* <div>
            <div className="page__muted" style={{ marginBottom: 4 }}>Equipment</div>
            <div style={{ position: 'relative' }}>
              <MultiSelect value={selectedEquipment} options={equipmentOptions} onChange={e => setSelectedEquipment(e.value)} placeholder={loadingEquipment ? "Loading..." : "Select equipment"} display="chip" maxSelectedLabels={2} selectedItemsLabel="{0} selected" style={{ width: '100%' }} disabled={loadingEquipment} />
              {loadingEquipment && (
                <ProgressSpinner style={{ width: '20px', height: '20px', position: 'absolute', right: '40px', top: '50%', transform: 'translateY(-50%)' }} />
              )}
            </div>
          </div> */}
          <div>
            <div className="page__muted" style={{ marginBottom: 4 }}>Locations</div>
            <div style={{ position: 'relative' }}>
              <MultiSelect value={selectedProperties} options={propertyOptions} onChange={e => setSelectedProperties(e.value)} placeholder={loadingProperties ? "Loading..." : "Select locations"} display="chip" maxSelectedLabels={2} selectedItemsLabel="{0} selected" style={{ width: '100%' }} disabled={loadingProperties} />
              {loadingProperties && (
                <ProgressSpinner style={{ width: '20px', height: '20px', position: 'absolute', right: '40px', top: '50%', transform: 'translateY(-50%)' }} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default Step2;
